// eslint-disable-next-line import/named
import { Location, LocationCompressed, LocationMapDto } from '../model';
import { axiosManager } from '../config';
import { JxtLocationTypeEnum } from '../types';
import { onNextPage } from '../utils';

export interface ILocationOptions {
  from?: string;
  to?: string;
  capacity?: number;
  labels?: string[];
  resourceTypeId?: number[];
  bookable?: boolean;
  visible?: boolean;
  name?: string;
  ownerId?: number;
  locationId?: number[];
  requestOwnerValidation?: boolean;
  favoritesOnly?: boolean;
}

export interface IGetLocationsParameters {
  name?: string;
  globalSearch?: string;
  type?: JxtLocationTypeEnum;
  locationId?: number[];
  managed?: boolean;
  page?: string;
  size?: number;
  favoritesOnly?: boolean;
}

export const LocationService = {
  path: 'locations',

  getLocation(id: number): Promise<Location> {
    const url = `${this.path}/${id}`;

    return axiosManager
      .getInstance()
      .get(url)
      .then((res) => res.data);
  },

  getLocationsWithPagination(options?: IGetLocationsParameters): Promise<{
    data: LocationCompressed[];
    nextPage: string;
  }> {
    const params = this.createLocationsWithPaginationParams(options);
    const page = params?.get('page');

    if (page) {
      return axiosManager.getInstance().get(page).then(onNextPage);
    }

    const url = `${this.path}`;

    return axiosManager.getInstance().get(url, { params }).then(onNextPage);
  },

  searchLocationMap(options: ILocationOptions): Promise<LocationMapDto[]> {
    const url = `${this.path}/map`;
    return axiosManager
      .getInstance()
      .get<LocationMapDto[]>(url, {
        params: this.createSearchLocationMapParams(options),
      })
      .then((res) => res.data);
  },

  searchLocationsWithPagination(params?: URLSearchParams): Promise<{
    data: LocationCompressed[];
    nextPage: string;
  }> {
    const page = params?.get('page');

    if (page) {
      return axiosManager.getInstance().get(page).then(onNextPage);
    }

    const url = `${this.path}/search`;

    return axiosManager.getInstance().get(url, { params }).then(onNextPage);
  },

  createSearchLocationMapParams(options: ILocationOptions): URLSearchParams {
    const computedParams = new URLSearchParams();

    if (options.name) {
      computedParams.append('name', options.name);
    }

    if (options.from && options.to) {
      if (options.from > options.to) {
        throw new Error('The start date should be before the end date');
      }

      computedParams.append('from', options.from);
      computedParams.append('to', options.to);
    }

    if (options.capacity) {
      computedParams.append('capacity', options.capacity.toString());
    }

    if (options.labels?.length && options.labels.length > 0) {
      computedParams.append('labels', options.labels.join(','));
    }

    if (options.resourceTypeId?.length && options.resourceTypeId?.length > 0) {
      computedParams.append('resourceTypeId', options.resourceTypeId.toString());
    }

    if (options.locationId?.length && options.locationId?.length > 0) {
      computedParams.append('locationId', options.locationId.toString());
    }

    if (options.ownerId !== null && options.ownerId !== undefined) {
      computedParams.append('ownerId', options.ownerId.toString());
    }

    if ('bookable' in options && typeof options.bookable === 'boolean') {
      computedParams.append('bookable', options.bookable.toString());
    }

    if ('visible' in options && typeof options.visible === 'boolean') {
      computedParams.append('visible', options.visible.toString());
    }

    if ('requestOwnerValidation' in options && typeof options.requestOwnerValidation === 'boolean') {
      computedParams.append('requestOwnerValidation', options.requestOwnerValidation.toString());
    }

    if (typeof options?.favoritesOnly === 'boolean') {
      computedParams.append('favoritesOnly', options.favoritesOnly.toString());
    }

    return computedParams;
  },

  createLocationsWithPaginationParams(options?: IGetLocationsParameters): URLSearchParams {
    const computedParams = new URLSearchParams();

    if (options?.name) {
      computedParams.append('name', options.name);
    }

    if (options?.globalSearch) {
      computedParams.append('globalSearch', options.globalSearch);
    }

    if (options?.type) {
      computedParams.append('type', options.type);
    }

    if (options?.locationId?.length && options.locationId.length > 0) {
      computedParams.append('locationId', options.locationId.toString());
    }

    if (typeof options?.managed === 'boolean') {
      computedParams.append('managed', options.managed.toString());
    }

    if (options?.page) {
      computedParams.append('page', options.page);
    }

    if (options?.size) {
      computedParams.append('size', options.size.toString());
    }

    if (typeof options?.favoritesOnly === 'boolean') {
      computedParams.append('favoritesOnly', options.favoritesOnly.toString());
    }

    return computedParams;
  },
};
